import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor, moduleCanDo } from "../lib/modules";
import { toast } from "sonner";
import { Plus, Truck, Search, CheckCircle2, FileText, Receipt, ExternalLink, Building2, MapPin, Phone, Mail, Eye, Filter, X as IconX, Package, Boxes, Tag, IndianRupee, Sparkles, History, RotateCcw, Pencil } from "lucide-react";
import { OrderDetailDialog } from "./OrderWorkflow";
import ClientImportantRemark from "../components/ClientImportantRemark";
import { DispatchModuleAccessButton, useReasonPrompt } from "../components/DispatchAuditControls";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import { useEditAccessRequest } from "../components/EditAccessRequest";

const STATUSES = ["Dispatched", "In Transit", "Delivered", "Recalled"];
const STATUS_COLOR = {
  "Dispatched": "bg-amber-100 text-amber-800",
  "In Transit": "bg-blue-100 text-blue-700",
  "Delivered": "bg-green-100 text-green-700",
  "Recalled": "bg-red-100 text-red-700",
};

export default function DispatchWorkflow() {
  const { user } = useAuth();
  const canAccess = user?.role === "master_admin" || moduleCanDo(user, "dispatch_orders", "view");
  const canEdit = user?.role === "master_admin" || moduleCanDo(user, "dispatch_orders", "edit");
  const isMaster = user?.role === "master_admin";
  // v109.8 — imperative reason prompt for edit/delete audit
  const { ask: askReason, dialog: reasonDialog } = useReasonPrompt();
  // v109.9.4 — Auto-open edit-access-request dialog on 409 for dispatch edits.
  const { dialog: editReqDialog, wrapMutation: wrapEditRequest } = useEditAccessRequest();
  const [viewMode] = useGlobalViewMode();

  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("pending");
  const [openingOrder, setOpeningOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  // Read-only "View Order Details" dialog payload — separate from the dispatch
  // management dialog so the team can inspect the complete order without
  // entering the dispatch action flow.
  const [viewingOrder, setViewingOrder] = useState(null);
  const [adding, setAdding] = useState(false);
  // Recall tracking dialog state — only meaningful post-Credit-Note.
  const [recallTarget, setRecallTarget] = useState(null);
  const [recallForm, setRecallForm] = useState({ reason: "", recall_tracking_no: "", recall_partner: "", recall_no_of_boxes: "", recall_cost: "", recall_remark: "" });
  // Edit dispatch dialog state (Master + Dispatch Admin)
  const [editingDispatch, setEditingDispatch] = useState(null);
  const [editForm, setEditForm] = useState({ tracking_no: "", tracking_partner: "", no_of_boxes: "", cost: "", remark: "" });
  // Filters (apply to both tabs)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [manageTab, setManageTab] = useState("overview");
  const [filters, setFilters] = useState({
    status: "",
    partner: "",
    recall: "",       // "" | "active" | "recalled"
    date_from: "",
    date_to: "",
    payment: "",      // "" | "Paid" | "Pending" | "Overdue" (Awaiting tab only)
  });
  const filtersActive = Object.values(filters).some((v) => !!v);
  const clearFilters = () => setFilters({ status: "", partner: "", recall: "", date_from: "", date_to: "", payment: "" });

  const openEditDispatch = (d) => {
    setEditingDispatch(d);
    setEditForm({
      tracking_no: d.tracking_no || "",
      tracking_partner: d.tracking_partner || "",
      no_of_boxes: d.no_of_boxes ?? "",
      cost: d.cost ?? "",
      remark: d.remark || "",
    });
  };
  const saveEditDispatch = async () => {
    if (!editingDispatch) return;
    const reason = await askReason({
      title: "Reason for editing dispatch",
      description: "Please state a brief reason for this change (recorded in the audit log).",
    });
    if (!reason) return;
    try {
      const payload = { ...editForm, change_reason: reason };
      const result = await wrapEditRequest(
        () => api.patch(`/dispatches/${editingDispatch.id}`, payload),
        { moduleKey: "dispatch_orders", recordId: editingDispatch.id, recordLabel: editingDispatch.tracking_no, proposedPayload: payload },
      );
      if (!result) { setEditingDispatch(null); return; }  // staged for approval
      toast.success("Dispatch updated");
      setEditingDispatch(null);
      if (openingOrder) openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    try {
      const [oRes, iRes] = await Promise.all([api.get("/orders?scope=all"), api.get("/invoices")]);
      const invIds = new Set(iRes.data.map((i) => i.order_id));
      const invoicedOrders = oRes.data.filter((o) => invIds.has(o.id) && (o.order_type || "Product").trim().toLowerCase() !== "service");
      setOrders(invoicedOrders);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  useEffect(() => { if (canAccess) load(); }, [canAccess]); // eslint-disable-line

  const pendingOrders = useMemo(() => {
    return orders.filter((o) => {
      if (filters.payment && o.payment_status !== filters.payment) return false;
      if (filters.date_from && (!o.created_at || new Date(o.created_at) < new Date(filters.date_from))) return false;
      if (filters.date_to && (!o.created_at || new Date(o.created_at) > new Date(`${filters.date_to}T23:59:59`))) return false;
      return true;
    });
  }, [orders, filters]);

  const awaitingOrders = useMemo(() => pendingOrders.filter((o) => !["Partial Delivered", "Complete Delivered"].includes(o.status)), [pendingOrders]);
  const partialOrders = useMemo(() => pendingOrders.filter((o) => o.status === "Partial Delivered"), [pendingOrders]);
  const deliveredOrders = useMemo(() => pendingOrders.filter((o) => o.status === "Complete Delivered"), [pendingOrders]);
  const ORDER_TABS = [
    { key: "pending", list: awaitingOrders, label: "Awaiting Dispatch", tag: "Pending Dispatch", cls: "flat-tag-red", empty: "No orders pending dispatch." },
    { key: "partial", list: partialOrders, label: "Partial Delivered", tag: "Partial Delivered", cls: "flat-tag-orange", empty: "No partially delivered orders." },
    { key: "delivered", list: deliveredOrders, label: "Delivered", tag: "Delivered", cls: "flat-tag-green", empty: "No delivered orders yet." },
  ];

  if (!canAccess) return <Navigate to="/" replace />;

  const openOrder = async (order) => {
    setOpeningOrder(order);
    setManageTab("overview");
    try {
      const { data } = await api.get(`/orders/${order.id}/full`);
      setOrderDetails(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Read-only Order Details viewer for Dispatch — reuses OrderDetailDialog
  // from OrderWorkflow. Edit/cancel/delete buttons are suppressed by not
  // passing canEdit/isMaster permission flags.
  const openViewDetails = async (order) => {
    try {
      const { data } = await api.get(`/orders/${order.id}/full`);
      setViewingOrder(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addDispatch = async () => {
    if (!form.tracking_no.trim()) { toast.error("Tracking number required"); return; }
    try {
      await api.post("/dispatches", { ...form, order_id: openingOrder.id });
      toast.success("Dispatch entry added");
      setForm(emptyForm());
      setAdding(false);
      openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const updateStatus = async (dispatch, newStatus) => {
    const reason = await askReason({
      title: `Change status to “${newStatus}”`,
      description: "Please state a brief reason for the status change (recorded in the audit log).",
    });
    if (!reason) return;
    try {
      const payload = { status: newStatus, change_reason: reason };
      const result = await wrapEditRequest(
        () => api.patch(`/dispatches/${dispatch.id}`, payload),
        { moduleKey: "dispatch_orders", recordId: dispatch.id, recordLabel: dispatch.tracking_no, proposedPayload: payload },
      );
      if (!result) return;
      toast.success("Status updated");
      openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markOrderComplete = async () => {
    try {
      const { data } = await api.post(`/orders/${openingOrder.id}/mark-complete`);
      const n = data?.dispatches_updated || 0;
      toast.success(`Order marked Complete Delivered${n ? ` · ${n} tracking entr${n === 1 ? "y" : "ies"} auto-updated to Delivered` : ""}`);
      openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Recall tracking — Dispatch Admin fills the return-leg tracking against a
  // dispatched row once a Credit Note has been issued.
  const openRecall = (d) => {
    setRecallTarget(d);
    setRecallForm({ reason: "", recall_tracking_no: "", recall_partner: "", recall_no_of_boxes: d.no_of_boxes || "", recall_cost: "", recall_remark: "" });
  };
  const submitRecall = async () => {
    if (!recallForm.reason.trim()) { toast.error("A recall reason is required"); return; }
    if (!recallForm.recall_tracking_no.trim()) { toast.error("Recall tracking number is required"); return; }
    try {
      await api.post(`/dispatches/${recallTarget.id}/recall`, recallForm);
      toast.success(`Tracking ${recallTarget.tracking_no} marked Recalled`);
      setRecallTarget(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const deleteDispatch = async (dispatch) => {
    const wasDispatched = false;
    const reason = await askReason({
      action: "delete",
      title: `Delete tracking ${dispatch.tracking_no}?`,
      description: wasDispatched
        ? "Products on this entry will be RESTORED back to Inventory. Please state a reason (recorded in the audit log). The record can be reverted from Dispatch → Logs within 30 days."
        : "The record will be soft-deleted (purged after 30 days). Please state a reason (recorded in the audit log).",
    });
    if (!reason) return;
    try {
      await api.delete(`/dispatches/${dispatch.id}`, { data: { delete_reason: reason } });
      toast.success(wasDispatched ? "Deleted — inventory restored" : "Deleted");
      openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const requestDoc = async (type) => {
    if (!openingOrder) return;
    if (!window.confirm(`Request ${type === "bill_copy" ? "Bill Copy" : "Quote"} URL from Accounts/Invoice team for ${openingOrder.order_no}?`)) return;
    try {
      await api.post("/dispatch-doc-requests", { order_id: openingOrder.id, request_type: type });
      toast.success("Request sent — Accounts will share the URL shortly");
      openOrder(openingOrder);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };


  const recallDispatch = async (dispatch) => {
    const reason = window.prompt(`Mark dispatch ${dispatch.tracking_no} as Product Recalled?\n\nEnter recall reason (required):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A recall reason is required"); return; }
    try {
      await api.post(`/dispatches/${dispatch.id}/recall`, { reason: reason.trim() });
      toast.success(`Dispatch ${dispatch.tracking_no} marked as Recalled`);
      openOrder(openingOrder);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1500px] space-y-6 flat-scope" data-testid="dispatch-flat-root">
      <PageHeader
        moduleKey="dispatch_orders"
        tone="amber"
        icon={Truck}
        eyebrow="Dispatch Workflow"
        title="Dispatch & Logistics"
        subtitle="Add tracking entries for invoiced orders. Multiple shipments per order supported."
        actions={
          <DispatchModuleAccessButton moduleKey="dispatch_orders" label="Dispatch & Logistics" />
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant={filtersActive ? "default" : "outline"}
          onClick={() => setFiltersOpen(true)}
          className="h-10"
          data-testid="dsp-filters-btn"
        >
          <Filter size={13} className="mr-1.5" /> Filters
          {filtersActive && <Badge className="ml-1.5 bg-white text-amber-700 text-[9px] px-1.5">on</Badge>}
        </Button>
        {filtersActive && (
          <Button variant="ghost" onClick={clearFilters} className="h-10 text-[12px] text-slate-500" data-testid="dsp-filters-clear">
            <IconX size={12} className="mr-1" /> Clear
          </Button>
        )}
        {filtersActive && (
          <div className="flex flex-wrap gap-1.5 items-center text-[11px]">
            {filters.status && <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800">Status: {filters.status}</Badge>}
            {filters.partner && <Badge variant="outline" className="bg-blue-50 border-blue-200 text-blue-800">Partner: {filters.partner}</Badge>}
            {filters.recall && <Badge variant="outline" className="bg-rose-50 border-rose-200 text-rose-800">{filters.recall === "active" ? "Active only" : "Recalled only"}</Badge>}
            {filters.payment && <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-800">Payment: {filters.payment}</Badge>}
            {(filters.date_from || filters.date_to) && <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-800">{filters.date_from || "—"} → {filters.date_to || "—"}</Badge>}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {tabAccessibleFor(user, "dispatch_orders", "pending") && ORDER_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`dsp-tab-${t.key}`}>{t.label} ({t.list.length})</TabsTrigger>
          ))}
        </TabsList>

        {ORDER_TABS.map((t) => (
        <TabsContent key={t.key} value={t.key} className="mt-4">
          {viewMode === "list" ? (
          <div className="pk-card overflow-x-auto">
            <table className="w-full text-sm responsive-table">
              <thead className="border-b border-slate-200 bg-slate-50/60">
                <tr><Th>Order No</Th><Th>Client</Th><Th>Value</Th><Th>Status</Th><Th>Payment</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody>
                {t.list.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">{t.empty}</td></tr>}
                {t.list.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100" data-testid={`dsp-order-${o.order_no}`} data-highlight-id={o.id}>
                    <td className="px-4 py-3 mono font-semibold" data-label="Order No">{o.order_no}</td>
                    <td className="px-4 py-3" data-label="Client">{o.client_name}</td>
                    <td className="px-4 py-3 mono" data-label="Value">₹ {o.total_value}</td>
                    <td className="px-4 py-3" data-label="Status"><span className={`flat-tag ${t.cls}`} data-testid={`dsp-status-tag-${o.order_no}`}>{t.tag}</span></td>
                    <td className="px-4 py-3" data-label="Payment"><Badge className={o.payment_status === "Paid" ? "bg-green-100 text-green-700" : o.payment_status === "Overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}>{o.payment_status}</Badge></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap" data-label="">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openViewDetails(o)}
                        className="h-8 text-xs mr-1.5"
                        data-testid={`dsp-view-${o.order_no}`}
                      >
                        <Eye size={12} className="mr-1" /> View Order Details
                      </Button>
                      {canEdit && (
                        <Button size="sm" onClick={() => openOrder(o)} className="h-8 text-xs bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid={`dsp-open-${o.order_no}`}>
                          <Truck size={12} className="mr-1" /> Manage
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`dsp-${t.key}-card-grid`}>
            {t.list.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-400 text-sm rounded-xl border border-dashed border-slate-200 bg-white">
                {t.empty}
              </div>
            )}
            {t.list.map((o) => (
              <div key={o.id} className="rounded-xl border border-slate-200 bg-white hover:shadow-lg transition-shadow p-3.5" data-testid={`dsp-pending-card-${o.order_no}`} data-highlight-id={o.id}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="mono font-bold text-amber-700 text-[12.5px] truncate">{o.order_no}</div>
                    <div className="text-[13px] font-bold text-slate-900 truncate">{o.client_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-400">Value</div>
                    <div className="mono font-extrabold text-slate-900 text-[13px]">₹ {Number(o.total_value || 0).toLocaleString("en-IN")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
                  <span className={`flat-tag ${t.cls}`} data-testid={`dsp-card-status-tag-${o.order_no}`}>{t.tag}</span>
                  <Badge className={`text-[10px] ${o.payment_status === "Paid" ? "bg-green-100 text-green-700" : o.payment_status === "Overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                    {o.payment_status}
                  </Badge>
                </div>
                <div className="flex items-center gap-1.5 pt-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" onClick={() => openViewDetails(o)} className="h-7 text-[10.5px] flex-1" data-testid={`dsp-card-view-${o.order_no}`}>
                    <Eye size={11} className="mr-1" /> Details
                  </Button>
                  {canEdit ? (
                    <Button size="sm" onClick={() => openOrder(o)} className="h-7 text-[10.5px] flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid={`dsp-card-open-${o.order_no}`}>
                      <Truck size={11} className="mr-1" /> Manage
                    </Button>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 text-[9.5px] ring-1 ring-slate-200 h-7 flex items-center px-2">View only</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}
        </TabsContent>
        ))}

      </Tabs>

      {/* v109.8 — imperative reason prompt (rendered once) */}
      {reasonDialog}
      {/* v109.9.4 — Edit access request dialog (opens on 409 outside window) */}
      {editReqDialog}

      {/* Order Dispatch Manage Dialog — Redesigned */}
      <Dialog open={!!openingOrder} onOpenChange={(o) => { if (!o) { setOpeningOrder(null); setOrderDetails(null); setAdding(false); } }}>
        <DialogContent className="flat-dialog max-w-5xl max-h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="dsp-manage-dialog">
          {/* ----- HERO HEADER (minimalist solid) ----- */}
          <DialogHeader className="px-5 sm:px-6 py-4 bg-amber-600 text-white border-b border-amber-700 shrink-0">
            <div className="flex items-start justify-between gap-4 pk-hero-stack">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center ring-1 ring-white/20 shrink-0">
                  <Truck size={20} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80 flex items-center gap-2 flex-wrap">
                    Order Dispatch Console
                    {orderDetails?.order?.status === "Complete Delivered" && (
                      <Badge className="bg-white/15 border border-white/25 text-white text-[9px] uppercase tracking-wider">
                        <CheckCircle2 size={9} className="mr-1" /> Delivered
                      </Badge>
                    )}
                  </div>
                  <DialogTitle className="text-[18px] sm:text-[20px] font-bold leading-tight mono break-all">
                    {openingOrder?.order_no || "—"}
                  </DialogTitle>
                  <div className="text-[12.5px] sm:text-[13px] opacity-95 mt-0.5 truncate font-medium flex items-center gap-1.5">
                    <Building2 size={12} /> {openingOrder?.client_name || "—"}
                  </div>
                </div>
              </div>
              {orderDetails?.order && (
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end pk-hero-stats">
                  <HeroStat label="Order Value" value={`₹ ${Number(orderDetails.order.total_value || 0).toLocaleString("en-IN")}`} />
                  <HeroStat label="Tracking" value={(orderDetails.dispatches || []).length} />
                  <HeroStat label="Invoices" value={(orderDetails.invoices || []).length} />
                  <HeroStat label="Payment" value={orderDetails.order.payment_status} accent={orderDetails.order.payment_status === "Paid" ? "bg-emerald-500/30" : "bg-rose-500/30"} />
                </div>
              )}
            </div>
          </DialogHeader>

          {orderDetails ? (
            <Tabs value={manageTab} onValueChange={setManageTab} className="flex-1 flex flex-col min-h-0">
              {/* Sticky Tab Strip */}
              <div className="px-6 pt-3 pb-2 border-b border-slate-200 bg-white shrink-0">
                <TabsList className="bg-slate-100 p-1 rounded-lg" data-testid="dsp-manage-tabs">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-amber-700 text-[12px] font-semibold px-4" data-testid="dsp-tab-overview">
                    <Building2 size={12} className="mr-1.5" /> Order Snapshot
                  </TabsTrigger>
                  <TabsTrigger value="tracking" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-amber-700 text-[12px] font-semibold px-4" data-testid="dsp-tab-tracking">
                    <Truck size={12} className="mr-1.5" /> Dispatch &amp; Tracking
                    <Badge className="ml-2 bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] px-1.5">{(orderDetails.dispatches || []).length}</Badge>
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 bg-slate-50/60">
                {/* ===== TAB 1 — OVERVIEW ===== */}
                <TabsContent value="overview" className="space-y-4 mt-0" data-testid="dsp-overview-pane">
                  <ClientImportantRemark remark={orderDetails.client?.important_remark || orderDetails.order.client_important_remark} />

                  {/* Client + Shipping side-by-side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dsp-client-panel">
                    <InfoCard tone="violet" icon={Building2} title="Client" testId="dsp-client-card">
                      <div className="font-bold text-slate-900 text-[14px] leading-snug">{orderDetails.order.client_name}</div>
                      <div className="text-[11px] text-slate-500 mono mt-1 flex items-center gap-1.5">
                        <Tag size={10} /> U-DISE · {orderDetails.order.u_dise || orderDetails.client?.u_dise || "—"}
                      </div>
                      <div className="text-[12px] text-slate-700 mt-2 inline-flex items-center gap-1.5 bg-white/70 border border-violet-200 rounded-md px-2 py-1">
                        <IndianRupee size={11} className="text-emerald-600" />
                        <span className="font-semibold">Order Value:</span>
                        <span className="mono text-emerald-700 font-bold">{Number(orderDetails.order.total_value || 0).toLocaleString("en-IN")}</span>
                      </div>
                    </InfoCard>
                    <InfoCard tone="blue" icon={MapPin} title="Shipping Address" testId="dsp-ship-card"
                      titleSuffix={(orderDetails.order.shipping_same_as_billing || !orderDetails.order.shipping_address) && (
                        <span className="text-[9px] normal-case font-medium text-blue-600 italic bg-white/70 border border-blue-200 rounded-full px-2 py-0.5">same as billing</span>
                      )}>
                      {(() => {
                        const sameAsBilling = orderDetails.order.shipping_same_as_billing || !orderDetails.order.shipping_address;
                        const addr = sameAsBilling
                          ? (orderDetails.order.billing_address || orderDetails.client?.address || "")
                          : (orderDetails.order.shipping_address || "");
                        const pin = sameAsBilling
                          ? (orderDetails.order.billing_pincode || orderDetails.client?.pin_code || "")
                          : (orderDetails.order.shipping_pincode || orderDetails.order.billing_pincode || "");
                        const district = sameAsBilling
                          ? (orderDetails.order.billing_district || orderDetails.client?.district || "")
                          : (orderDetails.order.shipping_district || orderDetails.order.billing_district || "");
                        const state = sameAsBilling
                          ? (orderDetails.order.billing_state || orderDetails.client?.state || "")
                          : (orderDetails.order.shipping_state || orderDetails.order.billing_state || "");
                        const districtState = [district, state].filter(Boolean).join(" · ");
                        return (
                          <>
                            <div className="text-[12.5px] text-slate-800 leading-snug font-medium">
                              {addr || <span className="text-slate-400 italic font-normal">— not specified</span>}
                            </div>
                            <div className="text-[11px] text-slate-600 mt-1.5 flex items-center gap-1.5">
                              {districtState && <span>{districtState}</span>}
                              {pin && (
                                <Badge variant="outline" className="bg-white border-blue-200 text-blue-700 text-[10px] mono">PIN {pin}</Badge>
                              )}
                            </div>
                          </>
                        );
                      })()}
                    </InfoCard>
                  </div>

                  {/* Order Contact strip */}
                  {(orderDetails.order.contact_name || orderDetails.order.contact_number) && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-3 flex-wrap" data-testid="dsp-order-contact">
                      <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <Phone size={14} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Order Contact for Dispatch</div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          <span className="text-[14px] font-bold text-slate-900">{orderDetails.order.contact_name || "—"}</span>
                          {orderDetails.order.contact_number && (
                            <a href={`tel:${orderDetails.order.contact_number}`} className="inline-flex items-center gap-1 text-[12px] mono text-emerald-700 font-semibold hover:underline">
                              <Phone size={11} /> {orderDetails.order.contact_number}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Invoices */}
                  <InfoCard tone="emerald" icon={Receipt} title={`Invoices · ${(orderDetails.invoices || []).length}`} testId="dsp-invoice-info"
                    titleSuffix={(
                      <Badge className={orderDetails.order.payment_status === "Paid" ? "bg-emerald-100 text-emerald-700 text-[10px]" : "bg-amber-100 text-amber-800 text-[10px]"}>
                        {orderDetails.order.payment_status}
                      </Badge>
                    )}>
                    {!orderDetails.invoices || orderDetails.invoices.length === 0 ? (
                      <div className="text-[12px] text-slate-500 italic">No invoices generated yet.</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="dsp-invoice-table">
                        {orderDetails.invoices.map((inv) => {
                          const cancelled = !!inv.is_cancelled;
                          const created = inv.created_at ? new Date(inv.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", year: "numeric", month: "short", day: "2-digit" }) : "—";
                          return (
                            <div key={inv.id} className={`rounded-md border bg-white p-2.5 ${cancelled ? "opacity-60 border-rose-200" : "border-emerald-200 hover:shadow-sm"} transition`} data-testid={`dsp-inv-${inv.invoice_no}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="mono font-bold text-[13px] text-slate-900">{cancelled ? <span className="line-through">{inv.invoice_no}</span> : inv.invoice_no}</div>
                                  <div className="text-[10px] text-slate-500 mono mt-0.5">{created}</div>
                                </div>
                                {cancelled
                                  ? <Badge className="bg-rose-100 text-rose-700 text-[9px]">Cancelled</Badge>
                                  : <Badge className={inv.is_paid ? "bg-emerald-100 text-emerald-700 text-[9px]" : "bg-amber-100 text-amber-700 text-[9px]"}>{inv.is_paid ? "Paid" : (inv.payment_status || "Pending")}</Badge>}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap mt-2 text-[10.5px]">
                                <span className="inline-flex items-center gap-1 text-slate-600">
                                  <IndianRupee size={9} className="text-emerald-600" />
                                  <span className="mono font-semibold text-slate-800">{inv.amount}</span>
                                </span>
                                {inv.eway_no && (
                                  <Badge variant="outline" className="bg-sky-50 border-sky-200 text-sky-800 text-[9px] mono">E-way {inv.eway_no}</Badge>
                                )}
                                {inv.dispatch_via && (
                                  <Badge variant="outline" className="bg-violet-50 border-violet-200 text-violet-800 text-[9px]">via {inv.dispatch_via}</Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </InfoCard>

                  {/* Doc Requests */}
                  <InfoCard tone="amber" icon={FileText} title="Document Requests" testId="dsp-doc-requests"
                    actions={(
                      <div className="flex items-center gap-1.5">
                        <Button onClick={() => requestDoc("bill_copy")} size="sm" variant="outline" className="h-8 text-[11px] bg-white border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="dsp-req-bill">
                          <Receipt size={11} className="mr-1" /> Bill Copy
                        </Button>
                        <Button onClick={() => requestDoc("quote")} size="sm" variant="outline" className="h-8 text-[11px] bg-white border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="dsp-req-quote">
                          <FileText size={11} className="mr-1" /> Quote
                        </Button>
                      </div>
                    )}>
                    {(orderDetails.doc_requests || []).length === 0 ? (
                      <div className="text-[11.5px] text-slate-500 italic">No requests yet. Click above to request a URL from Accounts.</div>
                    ) : (
                      <div className="space-y-1.5">
                        {orderDetails.doc_requests.map((dr) => (
                          <div key={dr.id} className="flex items-center justify-between gap-2 bg-white border border-amber-200 rounded-md px-2.5 py-1.5 text-[11px]" data-testid={`dsp-doc-req-${dr.id}`}>
                            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                              <Badge className={dr.status === "Fulfilled" ? "bg-emerald-100 text-emerald-700 text-[9px]" : "bg-amber-100 text-amber-800 text-[9px]"}>{dr.status}</Badge>
                              <span className="font-semibold text-slate-800">{dr.request_type === "bill_copy" ? "Bill Copy" : "Quote"}</span>
                              {dr.url && (
                                <a href={dr.url} target="_blank" rel="noreferrer" className="text-amber-700 underline truncate mono inline-flex items-center gap-1" data-testid={`dsp-doc-url-${dr.id}`}>
                                  <ExternalLink size={10} /> Open
                                </a>
                              )}
                              {dr.eway_no && <Badge className="bg-sky-100 text-sky-800 text-[9px] mono" data-testid={`dsp-doc-eway-${dr.id}`}>E-way {dr.eway_no}</Badge>}
                              {dr.eway_url && (
                                <a href={dr.eway_url} target="_blank" rel="noreferrer" className="text-sky-700 underline mono inline-flex items-center gap-1 text-[10px]">
                                  <ExternalLink size={10} /> E-way URL
                                </a>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-500 shrink-0">{dr.status === "Fulfilled" ? `by ${dr.fulfilled_by_name}` : `requested by ${dr.requested_by_name}`}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </InfoCard>

                  {/* Client Contacts */}
                  {orderDetails.client?.contacts && orderDetails.client.contacts.length > 0 && (
                    <InfoCard tone="slate" icon={Phone} title={`Client Contacts · ${orderDetails.client.contacts.length}`} testId="dsp-contacts">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {orderDetails.client.contacts.map((c, i) => (
                          <div key={i} className="bg-white border border-slate-200 rounded-md p-2.5 text-[12px] hover:shadow-sm transition" data-testid={`dsp-contact-${i}`}>
                            <div className="font-semibold text-slate-900 flex items-center gap-1.5 flex-wrap">
                              {c.name || "—"}
                              <Badge variant="outline" className="text-[9px] font-normal bg-slate-50 border-slate-200 text-slate-600">{c.type === "Other" ? c.type_other || "Other" : (c.type || "—")}</Badge>
                            </div>
                            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 text-slate-700 mono mt-1 hover:text-emerald-700"><Phone size={10} /> {c.phone}</a>}
                            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-slate-600 mono mt-0.5 truncate hover:text-blue-700"><Mail size={10} /> {c.email}</a>}
                          </div>
                        ))}
                      </div>
                    </InfoCard>
                  )}
                </TabsContent>

                {/* ===== TAB 2 — DISPATCH & TRACKING ===== */}
                <TabsContent value="tracking" className="space-y-4 mt-0" data-testid="dsp-tracking-pane">
                  {/* Toolbar */}
                  <div className="flex items-center justify-between flex-wrap gap-2 sticky top-0 z-10 bg-slate-50/95 pb-2 -mt-1 pt-1">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Tracking Entries</div>
                      <div className="text-[16px] font-bold text-slate-900 flex items-center gap-2">
                        {(orderDetails.dispatches || []).length}
                        <span className="text-[11px] text-slate-500 font-medium">on this order</span>
                      </div>
                    </div>
                    {canEdit ? (
                      <Button size="sm" onClick={() => setAdding(true)} className="h-9 text-xs bg-amber-600 hover:bg-amber-700 text-white shadow-sm" data-testid="dsp-add-button">
                        <Plus size={13} className="mr-1.5" /> Add Tracking Entry
                      </Button>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 text-[10px] ring-1 ring-slate-200">View only</Badge>
                    )}
                  </div>

                  {orderDetails.dispatches.length === 0 && !adding ? (
                    <div className="rounded-xl border-2 border-dashed border-amber-200 bg-white p-10 text-center">
                      <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-700 mx-auto flex items-center justify-center mb-3">
                        <Truck size={22} />
                      </div>
                      <div className="text-[14px] font-bold text-slate-900">No tracking entries yet</div>
                      <div className="text-[12px] text-slate-500 mt-1">Click <span className="font-semibold text-amber-700">Add Tracking Entry</span> above to record your first shipment.</div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="dsp-tracking-grid">
                      {orderDetails.dispatches.map((d) => {
                        const recalled = !!d.recalled;
                        return (
                          <div key={d.id} className={`rounded-xl border bg-white overflow-hidden hover:shadow-md transition ${recalled ? "opacity-70 border-rose-200" : "border-slate-200"}`} data-testid={`dsp-entry-${d.tracking_no}`}>
                            {/* Card top strip — tracking# + status */}
                            <div className={`px-3.5 py-2.5 flex items-center justify-between gap-2 ${recalled ? "bg-rose-50" : "bg-slate-50/80"} border-b border-slate-200`}>
                              <div className="min-w-0 flex-1">
                                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Tracking #</div>
                                <div className="mono font-bold text-[13.5px] text-slate-900 truncate flex items-center gap-1.5">
                                  {recalled ? <span className="line-through">{d.tracking_no}</span> : d.tracking_no}
                                  {recalled && <Badge className="bg-rose-100 text-rose-700 text-[9px]" data-testid={`dsp-recalled-${d.tracking_no}`}>Recalled</Badge>}
                                </div>
                              </div>
                              <div className="shrink-0">
                                {recalled ? (
                                  <Badge className="bg-rose-100 text-rose-700 text-[10px]">Recalled</Badge>
                                ) : canEdit ? (
                                  <Select value={d.status} onValueChange={(v) => updateStatus(d, v)}>
                                    <SelectTrigger className="h-8 text-[11px] w-32" data-testid={`dsp-status-${d.tracking_no}`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-700 text-[10px]">{d.status || "—"}</Badge>
                                )}
                              </div>
                            </div>
                            {/* Card body — meta grid + products */}
                            <div className="px-3.5 py-3 space-y-2.5">
                              <div className="grid grid-cols-3 gap-2 text-[11px]">
                                <MiniStat label="Partner" value={d.tracking_partner || "—"} icon={Truck} tone="blue" />
                                <MiniStat label="Boxes" value={d.no_of_boxes || "—"} icon={Boxes} tone="violet" mono />
                                <MiniStat label="Cost" value={d.cost ? `₹ ${d.cost}` : "—"} icon={IndianRupee} tone="emerald" mono />
                              </div>
                              {(d.item_sn || d.quantity) && (
                                <div className="text-[10.5px] text-slate-600 flex items-center gap-1.5">
                                  <Tag size={10} />
                                  <span className="mono">{d.item_sn || "—"}</span>
                                  <span className="text-slate-300">·</span>
                                  <span>Qty {d.quantity || "—"}</span>
                                </div>
                              )}
                            </div>
                            {/* Card actions */}
                            <div className="px-3.5 py-2 border-t border-slate-200 bg-slate-50/60 flex items-center justify-end gap-1.5 flex-wrap">
                              {!recalled && canEdit && (
                                <Button size="sm" variant="outline" onClick={() => openEditDispatch(d)} className="h-7 text-[10px]" data-testid={`dsp-edit-${d.tracking_no}`}>
                                  Edit Tracking
                                </Button>
                              )}
                              {!recalled && canEdit && (
                                <Button size="sm" variant="outline" onClick={() => recallDispatch(d)} className="h-7 text-[10px] text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`dsp-recall-${d.tracking_no}`}>
                                  Mark Recalled
                                </Button>
                              )}
                              {isMaster && (
                                <Button size="sm" variant="outline" onClick={() => deleteDispatch(d)} className="h-7 text-[10px] text-red-600 border-red-200 hover:bg-red-50" data-testid={`dsp-delete-${d.tracking_no}`}>
                                  Delete
                                </Button>
                              )}
                              {!canEdit && (
                                <Badge className="bg-slate-100 text-slate-500 text-[9.5px] ring-1 ring-slate-200">View only</Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {adding && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden shadow-sm" data-testid="dsp-add-form">
                      {/* Form Header */}
                      <div className="px-4 py-3 bg-amber-600 text-white flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                        <Truck size={16} />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold opacity-90">New Dispatch Entry</div>
                        <div className="text-[13px] font-semibold leading-tight">Record the shipment details.</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAdding(false)}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-white/20 transition"
                      data-testid="dsp-add-close"
                    >
                      <IconX size={16} />
                    </button>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Section 0: Invoice Reference (read-only summary) */}
                    {(orderDetails.invoices || []).length > 0 && (
                      <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3.5 shadow-sm" data-testid="dsp-add-invoice-ref">
                        <div className="flex items-center gap-2 mb-2.5">
                          <div className="w-7 h-7 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                            <Receipt size={13} />
                          </div>
                          <div>
                            <div className="text-[12px] font-bold text-slate-900">Invoice Reference</div>
                            <div className="text-[10px] text-slate-500">Use these values for the matching tracking entry — Accounts already captured them.</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {orderDetails.invoices.map((inv) => {
                            const cancelled = !!inv.is_cancelled;
                            return (
                              <div key={inv.id} className={`rounded-md border bg-white p-2.5 ${cancelled ? "opacity-60 border-rose-200" : "border-emerald-200"}`} data-testid={`dsp-add-invref-${inv.invoice_no}`}>
                                <div className="flex items-center justify-between gap-2 mb-1.5">
                                  <div className="min-w-0">
                                    <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-700">Invoice No.</div>
                                    <div className={`mono font-bold text-[13px] text-slate-900 ${cancelled ? "line-through" : ""}`}>{inv.invoice_no}</div>
                                  </div>
                                  {cancelled
                                    ? <Badge className="bg-rose-100 text-rose-700 text-[9px] shrink-0">Cancelled</Badge>
                                    : <Badge className={inv.is_paid ? "bg-emerald-100 text-emerald-700 text-[9px] shrink-0" : "bg-amber-100 text-amber-700 text-[9px] shrink-0"}>{inv.is_paid ? "Paid" : (inv.payment_status || "Pending")}</Badge>}
                                </div>
                                <div className="grid grid-cols-2 gap-1.5 text-[10.5px]">
                                  <div className="rounded bg-sky-50 border border-sky-200 px-2 py-1">
                                    <div className="text-[8.5px] uppercase tracking-wider font-bold text-sky-700">E-way No.</div>
                                    <div className="mono font-semibold text-slate-800 truncate" title={inv.eway_no || ""}>{inv.eway_no || <span className="text-slate-400 italic font-normal">—</span>}</div>
                                  </div>
                                  <div className="rounded bg-violet-50 border border-violet-200 px-2 py-1">
                                    <div className="text-[8.5px] uppercase tracking-wider font-bold text-violet-700">Dispatch Through</div>
                                    <div className="font-semibold text-slate-800 truncate" title={inv.dispatch_via || ""}>{inv.dispatch_via || <span className="text-slate-400 italic font-normal">—</span>}</div>
                                  </div>
                                  <div className="rounded bg-emerald-50 border border-emerald-200 px-2 py-1 col-span-2">
                                    <div className="text-[8.5px] uppercase tracking-wider font-bold text-emerald-700">Amount</div>
                                    <div className="mono font-bold text-emerald-800">₹ {inv.amount}</div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    )}

                    {/* Section 2: Shipment Details */}
                    <section className="rounded-lg border border-blue-200 bg-white p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-md bg-blue-100 text-blue-700 flex items-center justify-center">
                          <Truck size={13} />
                        </div>
                        <div className="text-[12px] font-bold text-slate-900">Shipment Details</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <F label="Tracking No. *">
                          <Input value={form.tracking_no} onChange={(e) => setForm({ ...form, tracking_no: e.target.value })} placeholder="e.g. AWB-987654" className="mono" data-testid="dsp-form-tracking" />
                        </F>
                        <F label="Tracking Partner">
                          <Input value={form.tracking_partner} onChange={(e) => setForm({ ...form, tracking_partner: e.target.value })} placeholder="e.g. BlueDart, DTDC, Delhivery" data-testid="dsp-form-partner" />
                        </F>
                        <F label="Item S/N">
                          <Input value={form.item_sn} onChange={(e) => setForm({ ...form, item_sn: e.target.value })} placeholder="e.g. SN-001, SN-002" data-testid="dsp-form-sn" />
                        </F>
                        <F label="Quantity">
                          <Input value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="Total units" data-testid="dsp-form-qty" />
                        </F>
                      </div>
                    </section>

                    {/* Section 3: Boxes / Cost / Status */}
                    <section className="rounded-lg border border-violet-200 bg-white p-3.5 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-7 h-7 rounded-md bg-violet-100 text-violet-700 flex items-center justify-center">
                          <Boxes size={13} />
                        </div>
                        <div className="text-[12px] font-bold text-slate-900">Logistics & Status</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <F label="No. of Boxes">
                          <Input value={form.no_of_boxes} onChange={(e) => setForm({ ...form, no_of_boxes: e.target.value })} placeholder="0" className="mono" data-testid="dsp-form-boxes" />
                        </F>
                        <F label="Cost Spent (INR)">
                          <div className="relative">
                            <IndianRupee size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0.00" className="mono pl-8" data-testid="dsp-form-cost" />
                          </div>
                        </F>
                        <F label="Status">
                          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="dsp-form-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </F>
                      </div>
                      <div className="mt-3">
                        <F label="Remark" full>
                          <Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} placeholder="Any special note for this shipment…" data-testid="dsp-form-remark" />
                        </F>
                      </div>
                    </section>
                  </div>

                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      <Tag size={11} /> All fields except Tracking No. are optional.
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setAdding(false)} data-testid="dsp-form-cancel">Cancel</Button>
                      <Button size="sm" onClick={addDispatch} className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm" data-testid="dsp-form-submit">
                        <CheckCircle2 size={13} className="mr-1" /> Save Dispatch Entry
                      </Button>
                    </div>
                  </div>
                    </div>
                  )}
                </TabsContent>
              </div>
            </Tabs>
          ) : (
            <div className="flex-1 flex items-center justify-center p-12 text-slate-400 text-[13px]">Loading order details…</div>
          )}
          <DialogFooter className="px-6 py-3 border-t border-slate-200 bg-white shrink-0">
            {orderDetails && orderDetails.order.status !== "Complete Delivered" && orderDetails.dispatches.length > 0 && (
              <Button onClick={markOrderComplete} className="bg-green-600 hover:bg-green-700 text-white" data-testid="dsp-mark-complete">
                <CheckCircle2 size={14} className="mr-1" /> Mark Order Complete Delivered
              </Button>
            )}
            <Button variant="outline" onClick={() => { setOpeningOrder(null); setOrderDetails(null); setAdding(false); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Read-only View Order Details dialog — surfaces the full order
          (client, billing/shipping, products, change log, invoices,
          dispatches) for the Dispatch team. Edit/cancel/delete buttons are
          suppressed by leaving canEdit / isMaster unset. */}
      <OrderDetailDialog
        viewing={viewingOrder}
        onClose={() => setViewingOrder(null)}
        isMaster={false}
        canEdit={false}
        currentUserId={user?.id}
        onDelete={() => {}}
        onCancel={() => {}}
        onEdit={() => {}}
      />

      {/* Edit Dispatch Entry Dialog — Master Admin + Dispatch Admin */}
      <Dialog open={!!editingDispatch} onOpenChange={(o) => !o && setEditingDispatch(null)}>
        <DialogContent className="flat-dialog max-w-md" data-testid="dsp-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Edit Dispatch — {editingDispatch?.tracking_no}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Tracking Number *</Label>
              <Input value={editForm.tracking_no} onChange={(e) => setEditForm({ ...editForm, tracking_no: e.target.value })} className="mt-1.5 mono" data-testid="dsp-edit-tracking" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Tracking Partner</Label>
              <Input value={editForm.tracking_partner} onChange={(e) => setEditForm({ ...editForm, tracking_partner: e.target.value })} className="mt-1.5" data-testid="dsp-edit-partner" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">No. of Boxes</Label>
              <Input type="number" value={editForm.no_of_boxes} onChange={(e) => setEditForm({ ...editForm, no_of_boxes: e.target.value })} className="mt-1.5 mono" data-testid="dsp-edit-boxes" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Cost (₹)</Label>
              <Input type="number" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} className="mt-1.5 mono" data-testid="dsp-edit-cost" />
            </div>
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Remark</Label>
              <Textarea rows={2} value={editForm.remark} onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })} className="mt-1.5" data-testid="dsp-edit-remark" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDispatch(null)}>Cancel</Button>
            <Button onClick={saveEditDispatch} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dsp-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters Dialog */}
      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent className="flat-dialog max-w-md" data-testid="dsp-filters-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <Filter size={16} /> Filter Dispatch & Logistics
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Status (All Dispatch Entries)</Label>
              <Select value={filters.status || "__all"} onValueChange={(v) => setFilters({ ...filters, status: v === "__all" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="dsp-filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All statuses</SelectItem>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Tracking Partner contains</Label>
              <Input value={filters.partner} onChange={(e) => setFilters({ ...filters, partner: e.target.value })} placeholder="e.g. BlueDart, DTDC" className="mt-1.5" data-testid="dsp-filter-partner" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Recall</Label>
              <Select value={filters.recall || "__all"} onValueChange={(v) => setFilters({ ...filters, recall: v === "__all" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="dsp-filter-recall"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All entries</SelectItem>
                  <SelectItem value="active">Active only</SelectItem>
                  <SelectItem value="recalled">Recalled only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Payment Status (Awaiting Dispatch)</Label>
              <Select value={filters.payment || "__all"} onValueChange={(v) => setFilters({ ...filters, payment: v === "__all" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="dsp-filter-payment"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All</SelectItem>
                  <SelectItem value="Paid">Paid</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">From</Label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1.5 mono" data-testid="dsp-filter-from" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">To</Label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1.5 mono" data-testid="dsp-filter-to" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { clearFilters(); setFiltersOpen(false); }} data-testid="dsp-filter-reset">Reset</Button>
            <Button onClick={() => setFiltersOpen(false)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dsp-filter-apply">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recall Tracking Dialog — only available post-Credit-Note */}
      <Dialog open={!!recallTarget} onOpenChange={(o) => !o && setRecallTarget(null)}>
        <DialogContent className="flat-dialog max-w-md" data-testid="dsp-recall-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Recall Product — {recallTarget?.tracking_no}</DialogTitle>
            <div className="text-[11px] text-slate-500">
              Order <span className="font-semibold">{recallTarget?.order_no}</span> ·
              Client <span className="font-semibold">{recallTarget?.client_name}</span>
            </div>
            <div className="text-[10.5px] text-slate-500 mt-1 italic">
              Capture the return-leg courier details. This row will be marked Recalled.
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <F label="Reason *" full>
              <Textarea rows={2} value={recallForm.reason} onChange={(e) => setRecallForm({ ...recallForm, reason: e.target.value })} placeholder="Why is this being recalled?" data-testid="dsp-recall-reason" />
            </F>
            <F label="Recall Tracking No. *">
              <Input value={recallForm.recall_tracking_no} onChange={(e) => setRecallForm({ ...recallForm, recall_tracking_no: e.target.value })} placeholder="e.g. RT-12345" className="mono" data-testid="dsp-recall-tracking" />
            </F>
            <F label="Recall Partner">
              <Input value={recallForm.recall_partner} onChange={(e) => setRecallForm({ ...recallForm, recall_partner: e.target.value })} placeholder="e.g. BlueDart" data-testid="dsp-recall-partner" />
            </F>
            <F label="No. of Boxes">
              <Input value={recallForm.recall_no_of_boxes} onChange={(e) => setRecallForm({ ...recallForm, recall_no_of_boxes: e.target.value })} data-testid="dsp-recall-boxes" />
            </F>
            <F label="Recall Cost (INR)">
              <Input value={recallForm.recall_cost} onChange={(e) => setRecallForm({ ...recallForm, recall_cost: e.target.value })} data-testid="dsp-recall-cost" />
            </F>
            <F label="Remark" full>
              <Input value={recallForm.recall_remark} onChange={(e) => setRecallForm({ ...recallForm, recall_remark: e.target.value })} data-testid="dsp-recall-remark" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecallTarget(null)}>Cancel</Button>
            <Button onClick={submitRecall} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="dsp-recall-submit">
              Mark Recalled
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function emptyForm() {
  return { item_sn: "", quantity: "", cost: "", no_of_boxes: "", tracking_no: "", tracking_partner: "", status: "Dispatched", remark: "", line_items: [] };
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left pk-table-header ${className}`}>{children}</th>;
}
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


function HeroStat({ label, value, accent = "bg-white/10" }) {
  return (
    <div className={`${accent} rounded-lg px-3 py-1.5 border border-white/20 min-w-[80px]`}>
      <div className="text-[8.5px] uppercase tracking-[0.12em] font-semibold opacity-80 leading-tight">{label}</div>
      <div className="text-[13px] font-bold leading-tight mono mt-0.5">{value}</div>
    </div>
  );
}

const INFO_TONES = {
  violet: { border: "border-violet-200", bg: "bg-violet-50/60", icon: "bg-violet-100 text-violet-700", title: "text-violet-700" },
  blue: { border: "border-blue-200", bg: "bg-blue-50/60", icon: "bg-blue-100 text-blue-700", title: "text-blue-700" },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/60", icon: "bg-emerald-100 text-emerald-700", title: "text-emerald-700" },
  amber: { border: "border-amber-200", bg: "bg-amber-50/60", icon: "bg-amber-100 text-amber-700", title: "text-amber-700" },
  slate: { border: "border-slate-200", bg: "bg-slate-50/60", icon: "bg-slate-100 text-slate-700", title: "text-slate-700" },
};

function InfoCard({ tone = "slate", icon: Icon, title, titleSuffix, actions, children, testId }) {
  const t = INFO_TONES[tone] || INFO_TONES.slate;
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} p-3.5 shadow-sm`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={`w-7 h-7 rounded-md ${t.icon} flex items-center justify-center`}>
              <Icon size={13} />
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`text-[10.5px] uppercase tracking-wider font-bold ${t.title}`}>{title}</div>
            {titleSuffix}
          </div>
        </div>
        {actions}
      </div>
      <div>{children}</div>
    </div>
  );
}

const MINI_TONES = {
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  violet: "bg-violet-50 border-violet-200 text-violet-700",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
};

function MiniStat({ label, value, icon: Icon, tone = "blue", mono = false }) {
  const cls = MINI_TONES[tone] || MINI_TONES.blue;
  return (
    <div className={`rounded-md border ${cls} px-2 py-1.5`}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold opacity-80">
        {Icon && <Icon size={9} />}
        <span>{label}</span>
      </div>
      <div className={`text-[12px] font-bold mt-0.5 text-slate-800 truncate ${mono ? "mono" : ""}`} title={String(value)}>{value}</div>
    </div>
  );
}



// ============================================================================
// v109.8 — Dispatch Logs Tab (Master Admin only)
// ============================================================================
// Surfaces the audit trail across the 5 dispatch modules (Purchase, Issuance,
// Vendors, Inventory, Dispatch orders). Master Admin can revert any entry
// (undo update / restore soft-delete / soft-delete a create).
function DispatchLogsTab() {
  const [logs, setLogs] = useState([]);
  const [softDeleted, setSoftDeleted] = useState([]);
  const [subTab, setSubTab] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [reverting, setReverting] = useState(null);
  const [showDiffId, setShowDiffId] = useState(null);

  const MODULES = [
    { k: "all", l: "All Modules" },
    { k: "product_issuance", l: "Issuance" },
    { k: "vendor_management", l: "Vendors" },
    { k: "dispatch_orders", l: "Dispatch" },
  ];
  const ACTIONS = [
    { k: "all", l: "All Actions", cls: "bg-slate-100 text-slate-700 border-slate-200" },
    { k: "create", l: "Create", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    { k: "update", l: "Update", cls: "bg-blue-100 text-blue-700 border-blue-200" },
    { k: "delete", l: "Delete", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (moduleFilter !== "all") params.set("module", moduleFilter);
      if (actionFilter !== "all") params.set("action", actionFilter);
      params.set("limit", "300");
      const [logRes, sdRes] = await Promise.all([
        api.get(`/dispatch/logs?${params.toString()}`),
        api.get(`/dispatch/soft-deleted${moduleFilter !== "all" ? `?module=${moduleFilter}` : ""}`),
      ]);
      setLogs(logRes.data || []);
      setSoftDeleted(sdRes.data || []);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [moduleFilter, actionFilter]);

  const MODULE_ROUTE = {
    product_issuance: "/m/product_issuance",
    vendor_management: "/m/vendor_management",
    dispatch_orders: "/m/dispatch_orders",
  };

  const revert = async (log) => {
    const label = log.action === "delete" ? "restore the deleted record" : `undo this ${log.action}`;
    if (!window.confirm(`Revert this ${log.action} on ${log.module_label}?\n\nThis will ${label} back to its previous state.`)) return;
    setReverting(log.id);
    try {
      await api.post(`/dispatch/logs/${log.id}/revert`);
      const route = MODULE_ROUTE[log.module_key];
      toast.success(
        log.action === "delete"
          ? `Restored to ${log.module_label} — open the module to see it`
          : `Reverted — check ${log.module_label} for the updated values`,
        {
          action: route ? {
            label: `Open ${log.module_label}`,
            onClick: () => { window.location.href = route; },
          } : undefined,
          duration: 6000,
        }
      );
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setReverting(null);
    }
  };

  const restoreSoftDeleted = async (row) => {
    if (!window.confirm(`Restore this ${row.module_label} record from Recycle Bin?`)) return;
    try {
      await api.post(`/dispatch/soft-deleted/${row.module_key}/${row.id}/restore`);
      const route = MODULE_ROUTE[row.module_key];
      toast.success(`Restored — ${row.display || "record"} is back in ${row.module_label}`, {
        action: route ? {
          label: `Open ${row.module_label}`,
          onClick: () => { window.location.href = route; },
        } : undefined,
        duration: 6000,
      });
      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const ACTION_TONE = {
    create: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    update: "bg-blue-50 text-blue-700 ring-blue-200",
    delete: "bg-rose-50 text-rose-700 ring-rose-200",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">Module</span>
          {MODULES.map((m) => (
            <button
              key={m.k}
              onClick={() => setModuleFilter(m.k)}
              className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border transition-all ${moduleFilter === m.k ? "bg-slate-800 text-white border-transparent shadow-sm" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              data-testid={`dsp-logs-module-${m.k}`}
            >{m.l}</button>
          ))}
        </div>
        <div className="w-px h-4 bg-slate-200" />
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">Action</span>
          {ACTIONS.map((a) => (
            <button
              key={a.k}
              onClick={() => setActionFilter(a.k)}
              className={`h-7 px-2.5 text-[10.5px] font-bold rounded-full border transition-all ${actionFilter === a.k ? `${a.cls} border-transparent shadow-sm` : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
              data-testid={`dsp-logs-action-${a.k}`}
            >{a.l}</button>
          ))}
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="dsp-logs-sub-all">
            All Audit Entries ({logs.length})
          </TabsTrigger>
          <TabsTrigger value="soft-deleted" data-testid="dsp-logs-sub-recycle">
            Recycle Bin ({softDeleted.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-4">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
          {!loading && logs.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">No audit entries.</div>
          )}
          <div className="space-y-2">
            {logs.map((log) => (
              <div key={log.id} className={`relative rounded-xl border bg-white p-3 ${log.reverted ? "opacity-60" : ""}`} data-testid={`dsp-log-${log.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md ring-1 text-[10px] font-bold uppercase tracking-wider ${ACTION_TONE[log.action] || "bg-slate-50 text-slate-700 ring-slate-200"}`}>
                        {log.action}
                      </span>
                      <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wide">{log.module_label}</span>
                      <span className="text-[11.5px] text-slate-800 font-semibold">{log.summary || "—"}</span>
                      {log.reverted && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">REVERTED</span>
                      )}
                    </div>
                    <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
                      <span>{log.user_name || "—"}</span>
                      <span>·</span>
                      <span className="mono">{log.created_at?.replace("T", " ").slice(0, 16)}</span>
                    </div>
                    {log.reason && (
                      <div className="mt-1.5 text-[11.5px] text-slate-700 bg-slate-50 rounded-md px-2 py-1 border border-slate-100">
                        <span className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500 mr-1">Reason</span>
                        {log.reason}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {log.action === "update" && Object.keys(log.diff || {}).length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[10.5px]"
                        onClick={() => setShowDiffId(showDiffId === log.id ? null : log.id)}
                        data-testid={`dsp-log-diff-${log.id}`}
                      >
                        {showDiffId === log.id ? "Hide" : "Diff"}
                      </Button>
                    )}
                    {!log.reverted && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => revert(log)}
                        disabled={reverting === log.id}
                        className="h-7 text-[10.5px] text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                        data-testid={`dsp-log-revert-${log.id}`}
                      >
                        <RotateCcw size={11} className="mr-1" /> {reverting === log.id ? "Reverting…" : "Revert"}
                      </Button>
                    )}
                  </div>
                </div>
                {showDiffId === log.id && Object.keys(log.diff || {}).length > 0 && (
                  <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2 text-[11px] font-mono max-h-56 overflow-y-auto">
                    {Object.entries(log.diff).map(([k, v]) => (
                      <div key={k} className="flex flex-wrap gap-2 py-0.5">
                        <span className="text-slate-500 min-w-[100px]">{k}</span>
                        <span className="text-rose-700 line-through">{JSON.stringify(v.from)}</span>
                        <span className="text-slate-400">→</span>
                        <span className="text-emerald-700">{JSON.stringify(v.to)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="soft-deleted" className="mt-4">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
          {!loading && softDeleted.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm border border-dashed border-slate-200 rounded-xl">
              Recycle Bin is empty. Soft-deleted records are purged automatically after 30 days.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {softDeleted.map((row) => (
              <div key={`${row.module_key}-${row.id}`} className="relative rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50/40 via-white to-white p-3" data-testid={`dsp-recycle-${row.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">{row.module_label}</div>
                    <div className="text-[13px] font-bold text-slate-800 truncate">{row.display || "—"}</div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Deleted by <b>{row.deleted_by_name || "—"}</b> · <span className="mono">{(row.deleted_at || "").replace("T", " ").slice(0, 16)}</span>
                    </div>
                    {row.delete_reason && (
                      <div className="mt-1 text-[11.5px] text-slate-700 bg-rose-50/70 rounded-md px-2 py-1 border border-rose-100">
                        {row.delete_reason}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[9.5px] uppercase tracking-wider font-bold text-slate-500">Purges in</div>
                    <div className="text-[13px] font-extrabold text-rose-700">{row.days_until_purge ?? "—"} d</div>
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => restoreSoftDeleted(row)}
                    className="h-7 text-[10.5px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    data-testid={`dsp-recycle-restore-${row.id}`}
                  >
                    <RotateCcw size={11} className="mr-1" /> Restore
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

